import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MainTracker } from "../../PixelTrackers/MainTracker";
import { getWebTrackingValue, trackWebAppPromoClicked, trackWebAppPromoViewed } from "../../PixelTrackers/webAppPromo";
import useSeenOnScreen from "../../customHooks/useSeenOnScreen";
import { APP_PLACEMENTS, getAppPlatform } from "../../utils/appPlacements";
import {
  APP_INSTALL_PROMPT_READY_EVENT,
  AppInstallBarDecision,
  hasAppInstallBarDecision,
  saveAppInstallBarDecision,
} from "../../utils/appInstallBar";
import * as translator from "../../utils/translator";
import "./AndroidAppBar.css";

/** Chrome's BeforeInstallPromptEvent; not in the TS DOM lib. */
type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
};

/** QA stub while the Play listing is unpublished; REACT_APP_ENV since stage has NODE_ENV=production. */
const isPreview = () =>
  process.env.REACT_APP_ENV !== "production" && new URLSearchParams(window.location.search).has("preview_app_bar");

/** Read by .pin-below-app-bar so strips pinned at the top sit under the bar, not behind it. */
const HEIGHT_VAR = "--android-app-bar-height";

// DE only feature, so the copy lives in DE only.
const t = (key: string) => translator.translate("de", "default", `AndroidAppBar/${key}`);

/** Chrome draws no install bar itself, so this shows one for the caught beforeinstallprompt. */
const AndroidAppBar = () => {
  const [promptEvent, setPromptEvent] = useState<InstallPromptEvent | null>(null);
  const [pending, setPending] = useState(false);
  const decidedRef = useRef(false);
  // The ref blocks a second tap in the same tick; the state disables the buttons.
  const pendingRef = useRef(false);
  const [preview] = useState(isPreview);
  const barRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (getAppPlatform() !== "android") return;
    if (preview) {
      setPromptEvent(
        Object.assign(new Event("beforeinstallprompt"), {
          prompt: async () => undefined,
          userChoice: Promise.resolve({ outcome: "dismissed" }),
        })
      );
      return;
    }
    const pickUpPrompt = () => {
      if (decidedRef.current || hasAppInstallBarDecision()) return;
      const event = (window as any).__appInstallPrompt as InstallPromptEvent | undefined;
      if (event) setPromptEvent(event);
    };
    pickUpPrompt();
    window.addEventListener(APP_INSTALL_PROMPT_READY_EVENT, pickUpPrompt);
    return () => window.removeEventListener(APP_INSTALL_PROMPT_READY_EVENT, pickUpPrompt);
  }, [preview]);

  const visible = Boolean(promptEvent);

  // Layout effect, so a pinned page strip never paints behind the bar for a frame.
  useLayoutEffect(() => {
    const bar = barRef.current;
    if (!visible || !bar) return;
    const root = document.documentElement;
    const publish = () => root.style.setProperty(HEIGHT_VAR, `${bar.offsetHeight}px`);
    publish();
    // The copy can wrap to a second line, so the height changes on rotate.
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(publish);
    observer?.observe(bar);
    return () => {
      observer?.disconnect();
      root.style.removeProperty(HEIGHT_VAR);
    };
  }, [visible]);

  const trackingProps = useMemo(
    () => ({
      app_placement_id: APP_PLACEMENTS.W7.id,
      app_platform: getAppPlatform(),
      app_page: APP_PLACEMENTS.W7.page,
    }),
    []
  );

  // Shown to signed-out visitors too, so the uuid comes from web tracking, not a customer prop.
  const seenRef = useSeenOnScreen(visible && !preview, () =>
    trackWebAppPromoViewed("W7", getWebTrackingValue("uuid"), trackingProps)
  );
  const setBar = useCallback(
    (element: HTMLDivElement | null) => {
      barRef.current = element;
      seenRef(element);
    },
    [seenRef]
  );

  if (!visible || !promptEvent) return null;

  const decide = (decision: AppInstallBarDecision, outcome: string) => {
    if (decidedRef.current) return;
    if (preview) {
      setPromptEvent(null);
      return;
    }
    decidedRef.current = true;
    saveAppInstallBarDecision(decision);
    (window as any).__appInstallPrompt = undefined;
    setPromptEvent(null);
    MainTracker.track("custom", "app_placement_prompt_outcome", { ...trackingProps, outcome });
  };

  const handleInstall = async () => {
    if (pendingRef.current || decidedRef.current) return;
    if (preview) {
      setPromptEvent(null);
      return;
    }
    pendingRef.current = true;
    setPending(true);
    trackWebAppPromoClicked("W7", getWebTrackingValue("uuid"), t("install"), trackingProps);
    try {
      // prompt() works once per event; Chrome fires a fresh one on the next load.
      await promptEvent.prompt();
      const { outcome } = await promptEvent.userChoice;
      const decision: AppInstallBarDecision = outcome === "accepted" ? "accepted" : "dismissed";
      decide(decision, decision);
    } catch {
      // Chrome refused to open the dialog. Hide for this page only, no decision stored.
      (window as any).__appInstallPrompt = undefined;
      setPromptEvent(null);
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  return (
    <div ref={setBar} className="android-app-bar" data-testid="android-app-bar" role="region" aria-label={t("label")}>
      <img className="android-app-bar__icon" src="/android-chrome-192x192.png" alt="" width={40} height={40} />
      <div className="android-app-bar__text">
        <span className="android-app-bar__title">{t("title")}</span>
        <span className="android-app-bar__subtitle">{t("subtitle")}</span>
      </div>
      <button type="button" className="android-app-bar__install" onClick={handleInstall} disabled={pending}>
        {t("install")}
      </button>
      <button
        type="button"
        className="android-app-bar__close"
        aria-label={t("close")}
        disabled={pending}
        onClick={() => {
          if (!pendingRef.current) decide("dismissed", "closed");
        }}
      >
        &times;
      </button>
    </div>
  );
};

export default AndroidAppBar;
